/**
 * Bridge between the host's AstorThreadAdapter and assistant-ui's
 * useExternalStoreRuntime. Maps our flat AstorThreadMessage[] to assistant-ui's
 * ThreadMessageLike[] and routes onNew/onEdit/onReload to the right adapter
 * callback.
 *
 * Streaming note: the `messages + convertMessage` path is preferred over
 * `messageRepository` here because assistant-ui's converter cache lets it
 * apply partial updates per chunk. Switching to the branchable-repository
 * path causes a full clear+reimport on every chunk, which manifests as a
 * stuck "empty assistant bubble" during streaming. Branch-tree restoration
 * across page reloads needs a different mechanism (hosted repository) and
 * is tracked as follow-up work.
 */

import {
  type AppendMessage,
  type ExternalStoreAdapter,
  type ThreadMessageLike,
  useExternalStoreRuntime,
  WebSpeechDictationAdapter,
  WebSpeechSynthesisAdapter,
} from '@assistant-ui/react';
import { useCallback, useMemo } from 'react';
import type { AstorThreadAdapter, AstorThreadMessage } from './types';

/**
 * Pull plain text out of an `AppendMessage` — assistant-ui sends content as
 * a parts array; we only consume the text parts.
 */
function extractText(message: AppendMessage): string {
  if (typeof message.content === 'string') {
    return message.content;
  }
  return message.content
    .filter((part) => part.type === 'text')
    .map((part) => ('text' in part ? part.text : ''))
    .join('');
}

/**
 * Normalize a possibly-blank id to undefined so the deterministic
 * astor-msg-${index} fallback kicks in. Empty strings sneak in via REST/JSON
 * boundaries where missing fields get coerced to '' rather than dropped.
 */
function normalizeId(id: string | undefined): string | undefined {
  if (!id) return undefined;
  const trimmed = id.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Map our AstorThreadMessage → assistant-ui's ThreadMessageLike. Tool results
 * become tool-call parts so MessagePrimitive.Parts can route them through the
 * Phase 2 registry. RAG sources are stuffed into metadata.custom.sources for
 * downstream consumers (the Sources strip is rendered as a custom slot).
 */
export function convertAstorMessage(message: AstorThreadMessage, index: number): ThreadMessageLike {
  const parts: Array<
    | { readonly type: 'text'; readonly text: string }
    | {
        readonly type: 'tool-call';
        readonly toolCallId: string;
        readonly toolName: string;
        readonly args: Record<string, never>;
        readonly result?: unknown;
        readonly isError?: boolean;
      }
  > = [];

  if (message.content) {
    parts.push({ type: 'text', text: message.content });
  }

  if (message.toolResults && message.role === 'assistant') {
    for (const tr of message.toolResults) {
      parts.push({
        type: 'tool-call',
        toolCallId: tr.toolCallId,
        toolName: tr.toolName,
        // We don't surface the tool's input args to the UI today (tool-call args
        // were only ever shown for debug); empty object satisfies the contract.
        args: {},
        result: tr.data,
        isError: typeof tr.error === 'string',
      });
    }
  }

  // Empty messages still need at least one part — assistant-ui validates this.
  if (parts.length === 0) {
    parts.push({ type: 'text', text: '' });
  }

  const id = normalizeId(message.id) ?? `astor-msg-${index}`;
  const createdAt =
    message.createdAt instanceof Date ? message.createdAt : message.createdAt ? new Date(message.createdAt) : undefined;

  // assistant-ui rejects `status` on non-assistant messages — only the
  // assistant role can be running/incomplete/complete. Setting it on user or
  // system messages crashes the render path, so gate the field strictly.
  const status: ThreadMessageLike['status'] | undefined =
    message.role === 'assistant'
      ? message.streaming
        ? { type: 'running' }
        : { type: 'complete', reason: 'stop' }
      : undefined;

  // Stuff host-side per-tool metadata (durationMs, pending, error) into
  // metadata.custom.toolResults so the AssistantMessage renderer can surface
  // the reasoning trace. assistant-ui's ToolCallMessagePart doesn't carry
  // duration/pending so we re-attach them here, keyed by toolCallId.
  const customFields: Record<string, unknown> = {};
  if (message.sources) customFields['sources'] = message.sources;
  if (message.toolResults && message.role === 'assistant') customFields['toolResults'] = message.toolResults;
  const metadata: ThreadMessageLike['metadata'] | undefined =
    Object.keys(customFields).length > 0 ? { custom: customFields } : undefined;

  return {
    role: message.role,
    content: parts,
    id,
    createdAt,
    ...(status ? { status } : {}),
    ...(metadata ? { metadata } : {}),
  };
}

interface UseAstorRuntimeOptions {
  enableVoice?: boolean;
}

/**
 * Create the assistant-ui runtime wired to an AstorThreadAdapter. Wraps
 * `useExternalStoreRuntime` so portals can stay agnostic of assistant-ui's
 * surface — they only ever pass the AstorThreadAdapter shape.
 */
export function useAstorRuntime(adapter: AstorThreadAdapter, options: UseAstorRuntimeOptions = {}) {
  const { enableVoice = false } = options;
  const { sendMessage, forkFromMessage, reloadFromMessage, cancelStream } = adapter;

  // WebSpeech adapters are stateless — instantiate once per `enableVoice` flip.
  const dictationAdapter = useMemo(() => (enableVoice ? new WebSpeechDictationAdapter() : undefined), [enableVoice]);
  const speechAdapter = useMemo(() => (enableVoice ? new WebSpeechSynthesisAdapter() : undefined), [enableVoice]);

  const onNew = useCallback(
    async (msg: AppendMessage) => {
      const text = extractText(msg);
      // assistant-ui sets parentId on AppendMessage when the user edits an
      // existing message or resubmits a regenerated one. sourceId is only set
      // when the user edited a specific message; its parent is what we fork from.
      if (msg.sourceId && msg.parentId && forkFromMessage) {
        await forkFromMessage(msg.parentId, text);
        return;
      }
      await sendMessage(text);
    },
    [sendMessage, forkFromMessage],
  );

  // onEdit / onReload / onCancel are wired only when the host adapter exposes the
  // corresponding callback. When omitted, assistant-ui suppresses the matching
  // action-bar button.
  const onEdit = useMemo(
    () =>
      forkFromMessage
        ? async (msg: AppendMessage) => {
            const text = extractText(msg);
            if (msg.parentId) {
              await forkFromMessage(msg.parentId, text);
              return;
            }
            await sendMessage(text);
          }
        : undefined,
    [sendMessage, forkFromMessage],
  );

  const onReload = useMemo(
    () =>
      reloadFromMessage
        ? async (parentId: string | null) => {
            if (!parentId) return;
            await reloadFromMessage(parentId);
          }
        : undefined,
    [reloadFromMessage],
  );

  const onCancel = useMemo(
    () =>
      cancelStream
        ? async () => {
            cancelStream();
          }
        : undefined,
    [cancelStream],
  );

  const adapters = useMemo(
    () => ({
      ...(dictationAdapter ? { dictation: dictationAdapter } : {}),
      ...(speechAdapter ? { speech: speechAdapter } : {}),
    }),
    [dictationAdapter, speechAdapter],
  );

  const externalAdapter = useMemo<ExternalStoreAdapter<AstorThreadMessage>>(
    () => ({
      messages: adapter.messages,
      isRunning: adapter.isStreaming,
      convertMessage: convertAstorMessage,
      onNew,
      onEdit,
      onReload,
      onCancel,
      adapters,
    }),
    [adapter.messages, adapter.isStreaming, onNew, onEdit, onReload, onCancel, adapters],
  );

  return useExternalStoreRuntime<AstorThreadMessage>(externalAdapter);
}
